import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/hooks/use-app-context";
import { useToast } from "@/hooks/use-toast";
import { Icon } from "@/lib/icons";
import { authApi } from "@/api/auth.api";
import { translations } from "@/translations/translations";
import {
  LanguagePreferencesFields,
  type LanguagePreferencesValue,
} from "@/components/profile/LanguagePreferencesFields";
import { getRequiredLanguageFields } from "@/lib/language-profile";
import { getPasswordCriteria } from "@/lib/signup.utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AuthHeading,
  AuthPasswordToggle,
  AuthShell,
  AuthSpinner,
  AUTH_FIELD_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
} from "@/features/auth-shell";

const ICON_CLASS = "pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground";

export default function ForceChangePassword() {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { user, setUser } = useApp();
  const { toast } = useToast();
  const currentLang = (lang || "fr") as "fr" | "en";
  const t = translations[currentLang]?.forceChangePassword ?? translations.fr.forceChangePassword;

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [languagePreferences, setLanguagePreferences] =
    useState<LanguagePreferencesValue>({
      spokenLanguages: user?.spokenLanguages ?? [],
      contributionLanguages: user?.contributionLanguages ?? [],
      moderationLanguages: user?.moderationLanguages ?? [],
    });

  const requiredLanguageFields = getRequiredLanguageFields(user?.role);
  const hasRequiredLanguages = requiredLanguageFields.every(
    (field) => languagePreferences[field].length > 0,
  );
  const passwordCriteria = getPasswordCriteria(password);
  const hasStrongPassword = Object.values(passwordCriteria).every(Boolean);
  const isValid =
    hasStrongPassword &&
    password === confirmPassword &&
    hasRequiredLanguages;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);
    try {
      const response = await authApi.forceChangePassword({
        password,
        preferredLocale: currentLang,
        ...languagePreferences,
      });
      const updated = {
        ...(user ?? response.user),
        ...response.user,
        ...languagePreferences,
        preferredLocale: currentLang,
        mustChangePassword: false,
        requiresLanguageSetup: false,
      };
      setUser(updated);
      localStorage.setItem("yodi-user", JSON.stringify(updated));
      toast({ title: t.success });
      navigate(`/${currentLang}`, { replace: true });
    } catch {
      toast({ title: t.error, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell documentTitle={t.title}>
      <AuthHeading title={t.title} description={t.description} />

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="force-change-password-form">
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-sm font-medium">
              {t.newPassword}
            </Label>
            <div className="relative">
              <Icon name="lock" className={ICON_CLASS} />
              <Input
                id="new-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={`pl-10 pr-11 ${AUTH_FIELD_CLASS}`}
                required
                minLength={8}
                aria-describedby="new-password-help"
              />
              <AuthPasswordToggle visible={showPassword} onToggle={() => setShowPassword(!showPassword)} />
            </div>
            {password.length > 0 && !hasStrongPassword ? (
              <p id="new-password-help" className="text-xs text-danger">
                {t.passwordRequirements}
              </p>
            ) : (
              <p id="new-password-help" className="text-xs leading-relaxed text-muted-foreground">
                {t.hint}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-sm font-medium">
              {t.confirmPassword}
            </Label>
            <div className="relative">
              <Icon name="lock" className={ICON_CLASS} />
              <Input
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className={`pl-10 ${AUTH_FIELD_CLASS}`}
                required
                minLength={8}
                aria-invalid={confirmPassword.length > 0 && password !== confirmPassword ? true : undefined}
              />
            </div>
            {confirmPassword.length > 0 && password !== confirmPassword && (
              <p className="text-xs text-danger">{t.mismatch}</p>
            )}
          </div>
        </div>

        <section className="space-y-4 border-t border-border/70 pt-6" aria-labelledby="force-language-title">
          <div>
            <h2 id="force-language-title" className="text-lg font-semibold tracking-[-0.02em] text-foreground">
              {t.languageProfileTitle}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {t.languageProfileDescription}
            </p>
          </div>
          <LanguagePreferencesFields
            role={user?.role}
            value={languagePreferences}
            onChange={setLanguagePreferences}
            copy={translations[currentLang]}
            stacked
          />
          {!hasRequiredLanguages ? (
            <p className="text-xs text-brand">
              {t.languageProfileRequired}
            </p>
          ) : null}
        </section>

        <Button type="submit" disabled={!isValid || loading} className={AUTH_PRIMARY_BUTTON_CLASS}>
          {loading ? <AuthSpinner /> : <Icon name="check" className="h-4 w-4" />}
          {loading ? t.saving : t.submit}
        </Button>
      </form>
    </AuthShell>
  );
}
